
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { API_BASEPATH } from 'api';
import { artistCardPopulation, getArtists, getFollowedArtists, } from 'api/artists';
import classNames from 'classnames';
import { ArtistPreview, Props as ArtistPreviewProps, } from 'components/artists/ArtistPreview';
import { Artworks } from 'components/artworks/Artworks';
import { ArtistsCarousel } from 'components/collections/ArtistsCarousel';
import { Collection, CollectionsMap } from 'constants/collections';
import { useAuth } from 'contexts';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetServerSideProps } from 'next';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import qs from 'qs';
import { ComponentProps, ReactElement, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Artist, mapIArtistsToArtists } from 'utils/data';
const FiltersUpdate = dynamic(() => import('components/collections/FiltersUpdate').then(m => m.FiltersUpdate), { ssr: false });
type Props = {
    currentCollection: Collection;
    featuredArtists: Artist[];
};
const CollectionPage = ({ currentCollection, featuredArtists = [] }: Props) => {
    const { t } = useTranslation('collections');
    const { user } = useAuth();
    const { data: followedArtists } = useQuery(['following-artists', user], async () => {
        return await getFollowedArtists({ pageSize: '100' });
    }, {
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        enabled: Boolean(user && featuredArtists?.length)
    });
    const getArtistPreviewProps = useCallback((artist: Artist): ComponentProps<typeof ArtistPreview> => ({
        artist,
        followId: followedArtists?.results?.find(followedArtist => followedArtist?.artist?.id === artist?.id)?.id,
        className: 'justify-between',
        buttonSize: 'sm'
    }), [followedArtists]);
    return (<>
      <Head>
        <title>{t(`${currentCollection}.pageTitle`)}</title>
        <meta name='description' content={t(`${currentCollection}.pageDescription`)}/>
      </Head>

      <section className='bg-[#EEEDED]'>
        <div className='container flex flex-col space-y-2.5 py-6 sm:py-8 lg:flex-row lg:items-center lg:justify-between lg:space-x-2.5 lg:space-y-0 xl:py-10'>
          <h1 className='w-max whitespace-nowrap font-body text-2xl font-medium tracking-normal xl:font-display xl:text-3xl'>
            {t(`${currentCollection}.title`)}
          </h1>

          <p className='max-w-[52.1875rem] whitespace-pre-line text-sm text-cool-600 xl:text-cool-800'>
            {t(`${currentCollection}.description`)}
          </p>
        </div>
      </section>

      {Boolean(featuredArtists?.length) && (<section className='bg-warm-50'>
          <div className='container py-6 md:py-8'>
            <h2 className='mb-5 font-body text-lg font-medium tracking-normal'>
              {t('featured_artists')}
            </h2>

            <div className='relative'>
              {[1, 2].map(i => (<div key={i} className={classNames('absolute right-[32.5%] hidden h-full w-[1px] bg-[#EEEDED]', i === 1
                    ? 'left-1/2 md:block lg:left-[32.5%]'
                    : 'right-[32.5%] lg:block')}/>))}

              <div className='md:hidden'>
                <ArtistsCarousel featuredArtists={featuredArtists} getArtistPreviewProps={getArtistPreviewProps}/>
              </div>

              <ol className='hidden grid-cols-2 gap-x-8 gap-y-4 md:grid lg:grid-cols-3 xl:gap-x-16'>
                {featuredArtists
                .slice(0, featuredArtists.length < 6 ? 3 : 6)
                .map(artist => (<li key={artist.id}>
                      <div className='xl:hidden'>
                        <ArtistPreview size='sm' {...getArtistPreviewProps(artist)}/>
                      </div>

                      <div className='hidden xl:block'>
                        <ArtistPreview {...getArtistPreviewProps(artist)}/>
                      </div>
                    </li>))}
              </ol>
            </div>
          </div>
        </section>)}

      <Artworks instantSearch={{
            onStateChange({ uiState, setUiState }) {
                setUiState(uiState);
            }
        }} hideBanner>
        <FiltersUpdate initialIndexUiState={CollectionsMap[currentCollection].initialFilterState}/>
      </Artworks>
    </>);
};
CollectionPage.getLayout = (page: ReactElement) => (<BaseLayout>{page}</BaseLayout>);
const fetchFeaturedArtists = async (collectionName: Collection): Promise<ArtistPreviewProps['artist'][]> => {
    const filters: Record<string, unknown> = {};
    try {
        const artistsCollectionsRes = await fetch(`${API_BASEPATH}/collections-artists.json`);
        const artistsCollections: {
            name: string;
            ids: string[];
        }[] = await artistsCollectionsRes.json();
        const currentCollectionData = artistsCollections.find(item => item.name === collectionName);
        const artistsIds = currentCollectionData?.ids;
        if (!artistsIds?.length)
            return [];
        filters.id = { $in: artistsIds };
    }
    catch (err) {
        console.error(err);
        return [];
    }
    const query = qs.stringify({
        ...artistCardPopulation,
        filters,
        pagination: {
            pageSize: 9
        }
    }, { encodeValuesOnly: true });
    const { data } = await getArtists(query);
    if (!data)
        return [];
    return mapIArtistsToArtists(data);
};
const getServerSideProps: GetServerSideProps<Props> = async ({ query, }) => {
    const { name, disableProd } = query as {
        name: string;
        disableProd: string;
    };
    if (!name)
        return { notFound: true };
    const collectionIndex = Object.values(CollectionsMap).findIndex(item => item.slug === name);
    if (collectionIndex < 0) {
        return { notFound: true };
    }
    const currentCollection = Object.keys(CollectionsMap)[collectionIndex] as Collection;
    const featuredArtists = await fetchFeaturedArtists(currentCollection);
    return {
        props: {
            currentCollection,
            featuredArtists
        }
    };
};
// export const getStaticPaths: GetStaticPaths = () => {
//   return {
//     paths: Object.values(CollectionsMap).map(item => ({
//       params: { name: item.slug },
//     })),
//     fallback: false,
//   }
// }
export default CollectionPage;

    async function __Next_Translate__getServerSideProps__196d431c7fd__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/collections/[name]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__196d431c7fd__ as getServerSideProps }
  